<template>
  <div>
    <v-file-input
        ref="fileUpload"
        @change="handleImage"
        :label="'Upload ' + name"
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        :loading="loading"
        :rules="rules">
    </v-file-input>
    <small class="tw-w-full tw-p-2">maximum of {{ size }} MB. Supports PDF, JPEG, and PNG files</small>
  </div>
</template>

<script>
import axios from 'axios';
import nonResidentAuthService from '@/services/nonResidentAuth';

export default {
  name: 'ExpressUpload',
  data() {
    return {
      loading: false,
      value: null,
    };
  },
  props: {
    uid: {
      type: String,
      required: true,
    },
    phoneNumber: {
      type: String,
      required: true,
    },
    fullName: {
      type: String,
      required: true,
    },
    email: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    size: {
      type: Number,
      default: 10,
    },
    bucket: {
      type: String,
      default: 'gi-policies',
      required: true,
    },
    companyPin: {
      type: String,
      required: false,
    },
    kraPin: {
      type: String,
      required: false,
    },
    idNumber: {
      type: String,
      required: false,
    },
    travelType: {
      type: String,
      required: false,
    },
    documentType: {
      type: String,
      required: false,
    },
  },
  computed: {
    rules() {
      return [
        (v) => !v || (v && v.size <= this.size * 1024 * 1024) || `${this.name} must be less than ${this.size}MB`,
      ];
    },
  },
  methods: {
    async handleImage(file) {
      const uploadErrorTitle = 'Upload Error';
      try {
        if (file) {
          this.loading = true;
          if (/\.(jpe?g|png|pdf)$/i.test(file.name)) {
            const FileSize = file.size / 1024 / 1024;
            if (FileSize > this.size) {
              this.$notify.error(`Image greater than ${this.size} MB`, 'Image Error');
              this.loading = false;
              return;
            }

            const reader = new FileReader();
            // let base64result;
            this.uploadData = await new Promise((resolve) => {
              reader.onload = () => {
                const base64result = /,(.+)/.exec(reader.result)[1];
                resolve({
                  fileName: file.name,
                  kraPinNumber: this.kraPin || this.companyPin || this.uid,
                  idNumber: this.idNumber,
                  img64: base64result,
                  bucket: this.bucket,
                  phoneNumber: this.phoneNumber,
                  email: this.email,
                  fullName: this.fullName,
                  travelType: this.travelType,
                  documentType: this.documentType,
                });
              };
              reader.readAsDataURL(file);
            });

            const authFailedMessage = 'Authentication failed. Please try again.';
            try {
              // For authenticated users, let axios interceptor handle auth headers
              // For non-authenticated users (non-residents), use nonResidentAuthService
              const isAuthenticated = this.$store.getters['auth/isAuthenticated'];
              let requestConfig = {};

              if (!isAuthenticated) {
                const headers = await nonResidentAuthService.getAuthHeaders();
                requestConfig = { headers, timeout: 60000 }; // 60 second timeout
              } else {
                requestConfig = { timeout: 60000 }; // 60 second timeout for authenticated users
              }
              // If authenticated, axios interceptor will automatically add auth headers

              try {
                // eslint-disable-next-line no-underscore-dangle
                requestConfig._skipErrorRedirect = true;
                await axios.post('/customer/quotation/upload', this.uploadData, requestConfig);
                // console.log('Upload successful:', response.data);
                this.$notify.success('Documents uploaded successfully', 'Success');
                this.$emit('upload-success', { documentType: this.documentType, name: this.name });
                this.loading = false;
              } catch (uploadError) {
                // Prevent the error from propagating and crashing the page
                this.handleUploadError(uploadError);
                return; // Exit early to prevent further execution
              }
            } catch (error) {
              // Handle both auth errors and other unexpected errors
              this.loading = false;
              if (error.message && error.message.includes('auth')) {
                this.$notify.error(authFailedMessage, uploadErrorTitle);
                // console.error('Non-resident authentication error:', error);
              } else {
                this.handleUploadError(error);
              }
              this.resetFileInput();
            }
          } else {
            this.$notify.error('Not Valid Document', 'Document Error');
            this.loading = false;
            if (this.$refs.fileUpload && this.$refs.fileUpload.reset) {
              this.$refs.fileUpload.reset();
            }
          }
        }
      } catch (error) {
        // Handle any unexpected errors in the main upload flow
        this.loading = false;
        this.$notify.error('An unexpected error occurred. Please try again.', uploadErrorTitle);
        this.resetFileInput();
        // console.error('Unexpected error in handleImage:', error);
      }
    },

    handleUploadError(error) {
      // Ensure loading is always set to false, even if other operations fail
      try {
        this.loading = false;
      } catch (loadingError) {
        // console.error('Failed to set loading state:', loadingError);
      }

      const errorTitle = 'Upload Error';

      try {
        // Use the interpreter for all other errors
        this.$notify.fromError(error, errorTitle);
      } catch (toastError) {
        // Fallback if even toast fails
      }

      // Try to reset file input, but don't let it crash if it fails
      try {
        this.resetFileInput();
      } catch (resetError) {
        // console.error('Failed to reset file input after upload error:', resetError);
      }

      // Log error for debugging but don't let it crash the app
      try {
        // console.error('Upload error handled:', error);
      } catch (logError) {
        // Even logging failed, but we won't throw
      }
    },

    resetFileInput() {
      try {
        if (this.$refs.fileUpload && this.$refs.fileUpload.reset) {
          this.$refs.fileUpload.reset();
        }
      } catch (resetError) {
        // console.error('Failed to reset file input:', resetError);
      }
    },
  },
};
</script>
